'use client'

import { IMAGES_BACKEND_URL } from '@/utils/constants'
import { Project } from '@/utils/types/Project'
import { EmblaCarouselType, EmblaEventType, EmblaOptionsType } from 'embla-carousel'
import Autoplay from 'embla-carousel-autoplay'
import useEmblaCarousel from 'embla-carousel-react'
import { Link } from '@/i18n/routing'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import PlusIcon from '../../../../components/icons/PlusIcon'
import ImageWithFallback from '../../../../components/ui/ImageWithFallback'
import Card from '../../../../components/ui/Card'

const TWEEN_FACTOR_BASE = 0.5

const numberWithinRange = (number: number, min: number, max: number): number => Math.min(Math.max(number, min), max)

type PropType = {
  data: Project[]
  options?: EmblaOptionsType
}

const PortfolioSliceCarousel: React.FC<PropType> = (props) => {
  const { options } = props
  const [emblaRef, emblaApi] = useEmblaCarousel(options, [Autoplay({ playOnInit: true, delay: 3000, stopOnInteraction: false })])
  const tweenFactor = useRef(0)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const setTweenFactor = useCallback((emblaApi: EmblaCarouselType) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length
  }, [])

  const tweenOpacity = useCallback((emblaApi: EmblaCarouselType, eventName?: EmblaEventType) => {
    const engine = emblaApi.internalEngine()
    const scrollProgress = emblaApi.scrollProgress()
    const slidesInView = emblaApi.slidesInView()
    const isScrollEvent = eventName === 'scroll'

    emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
      let diffToTarget = scrollSnap - scrollProgress
      const slidesInSnap = engine.slideRegistry[snapIndex]

      slidesInSnap.forEach((slideIndex) => {
        if (isScrollEvent && !slidesInView.includes(slideIndex)) return

        if (engine.options.loop) {
          engine.slideLooper.loopPoints.forEach((loopItem) => {
            const target = loopItem.target()

            if (slideIndex === loopItem.index && target !== 0) {
              const sign = Math.sign(target)

              if (sign === -1) {
                diffToTarget = scrollSnap - (1 + scrollProgress)
              }
              if (sign === 1) {
                diffToTarget = scrollSnap + (1 - scrollProgress)
              }
            }
          })
        }
        const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor.current)
        const opacity = numberWithinRange(tweenValue, 0, 1).toString()
        emblaApi.slideNodes()[slideIndex].style.opacity = opacity
      })
    })
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    setTweenFactor(emblaApi)
    tweenOpacity(emblaApi)
    emblaApi.on('reInit', setTweenFactor).on('reInit', tweenOpacity).on('scroll', tweenOpacity).on('slideFocus', tweenOpacity)
  }, [emblaApi, tweenOpacity, setTweenFactor])

  return (
    <div className="w-full overflow-hidden" ref={emblaRef}>
      <div className="slides w-full flex touch-pan-y touch-pinch-zoom -ml-4">
        {props.data.map((project, index) => (
          <Link href={{ pathname: '/projekte/[slug]', params: { slug: project.slug } }} key={project.id} className="flex-none min-w-[312px] w-[80%] pl-8 group">
            <div className="flex flex-col">
              <Card className={`p-0! overflow-hidden rounded-2xl ${selectedIndex === index ? 'border border-accent' : ''}`}>
                <ImageWithFallback
                  className="min-h-[300px] w-full h-full object-cover"
                  width={1200}
                  height={900}
                  src={IMAGES_BACKEND_URL + 'projects/' + project.id + '/' + project.main_image}
                  fallbackSrc="/images/mocks/mock-bg.jpg"
                  alt={project.name + '`s image'}
                />
              </Card>
              <div className="mt-8">
                <PlusIcon size={20} color={'#fff'} className="w-5 h-5 object-cover" />
                <p className="mt-8 text-white leading-6 text-lg font-light">
                  <strong className="font-medium">{project.name}</strong>
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}

export default PortfolioSliceCarousel
